import React, { useContext, useEffect, useState } from 'react';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilDialog from '@components/RingilComponents/RingilDialog.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import SvgGrayInfo from '@components/ringil3/icons/GrayInfo.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import LocationSelect from '@components/framework/locationselect/LocationSelect.tsx';
import { isNotBlank } from '@utils/utils.tsx';

interface ComponentProps {
  visible: boolean;
  /** The location being replaced. */
  locationId: string;
  locationName: string;
  onHide: () => void;
  onCompleted: () => void;
}

/**
 * Replaces a location by another one: the old location is deleted and anything that would re-use it
 * resolves to the replacement.
 *
 * The already-booked future reservations are shown before confirming — superseding does not cancel
 * them, they stay valid on the old location and only *new* bookings route to the replacement.
 *
 * Nothing is copied from the old location and the replacement's own configuration (visibility,
 * timeslot settings, contacts, …) is not compared or warned about — it is the user's to set up.
 */
const SupersedeLocationDialog: React.FC<ComponentProps> = ({ visible, locationId, locationName, onHide, onCompleted }) => {
  const { tr } = useTranslationLgs();
  const { supersedeCompanyLocation, getCompanyLocationFutureReservationsCount } = useContext(ApiContext);

  const [supersededByLocationId, setSupersededByLocationId] = useState<string>(null);
  const [futureReservationsCount, setFutureReservationsCount] = useState<number>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setSupersededByLocationId(null);
    setFutureReservationsCount(null);
    setSaving(false);
    getCompanyLocationFutureReservationsCount(locationId, data => setFutureReservationsCount(data.futureReservationsCount));
  }, [visible, locationId]);

  const save = () => {
    setSaving(true);
    supersedeCompanyLocation(
      locationId,
      { supersededByLocationId },
      () => {
        setSaving(false);
        onCompleted();
      },
      null,
      {
        onError: (_error, showStandardError) => {
          showStandardError();
          setSaving(false);
        },
      },
    );
  };

  return (
    <RingilDialog
      visible={visible}
      heading={tr('LocalityList.supersedeLocationTitle', 'Replace location')}
      onHide={onHide}
      size={'md'}
      dataTestId={'location-supersede-dialog'}
      footer={
        <>
          <RingilButton
            mode={'filled-rectangle'}
            size={'big'}
            label={tr('LocalityList.supersedeLocationConfirm', 'Replace location')}
            disabled={!isNotBlank(supersededByLocationId) || supersededByLocationId === locationId}
            loading={saving}
            onClick={save}
            dataTestId={'location-supersede-confirm'}
          />
          <RingilButton mode={'link'} size={'big'} label={tr('generic.cancel', 'Cancel')} disabled={saving} onClick={onHide} />
        </>
      }>
      <div className={'flex flex-col gap-6'}>
        <COInlineMessage variant={'info'} icon={<SvgGrayInfo />} buttons={null} dataTestId={'location-supersede-info'}>
          {tr(
            'LocalityList.supersedeLocationMessage',
            'The location {{locationName}} will be deleted and will no longer be offered when creating new reservations and shipments. Anything that would have used it resolves to the replacement.',
            { locationName },
          )}
        </COInlineMessage>

        {futureReservationsCount > 0 && (
          <COInlineMessage variant={'warning'} icon={<SvgGrayInfo />} buttons={null} dataTestId={'location-supersede-futureReservations'}>
            {tr(
              'LocalityList.supersedeLocationFutureReservations',
              'There are {{count}} future reservations booked on this location. They stay valid and take place at this location — only new bookings will use the replacement.',
              { count: futureReservationsCount },
            )}
          </COInlineMessage>
        )}

        <Field label={tr('LocalityList.supersedeLocationReplacement', 'Replacement location')}>
          <LocationSelect
            value={supersededByLocationId}
            onChange={location => setSupersededByLocationId(location?.locationId)}
            fullWidth
            dataTestId={'location-supersede-select'}
          />
        </Field>
      </div>
    </RingilDialog>
  );
};

export default SupersedeLocationDialog;
